import React, { useEffect } from "react";
// react-feather is already this bundle's icon set. A real icon rather than a Unicode arrowhead
// (Chris: "the arrow is weird on this dropdown") — U+2304 is a MATHEMATICAL glyph, so its size and
// baseline are whatever each font decided, and next to 14px bold it rendered small and low.
import { ChevronDown, ChevronUp, Smartphone } from "react-feather";

import { createBrowserRouter, RouterProvider, useRouteError } from "react-router-dom";

import BasePage from "./BasePage";
import Form from "./Form";
import TermsAndConditions from "./TermsAndConditions";
import PageNotFound from "./PageNotFound";
import WaiverDetail from "./WaiverDetail";
import { API_URL } from "./config";
import { referrerHost, track, trackError } from "./beacon";
import { forgetEveryDraft } from "./draft";
import { viaFields } from "./qr-tag";
import { siteHref } from "./carry";
import { detectLang, enabledLangs, strings } from "./i18n";
import type { Strings } from "./i18n";
import { askTopUp, page as topUpPage, TOP_UP_WAIVERS, topUpOpen, worksAgain } from "./topUp";
// The price, from the one source (TW-0045). This file is a GENERATED byte-identical copy of
// pricing.json at the repo root — create-react-app refuses an import from outside src/, so the copy
// is how the root file reaches this bundle. Never hand-edit it: `./run.sh price-lint --fix` writes
// it and `./run.sh price-lint` checks it.
import pricing from "./pricing.json";
import { demoEarFromUrl, demoMinorsFromUrl, demoPlaceFromUrl, demoPlaceLabel, demoProofFromUrl, demoStateFromUrl, demoTypeFromUrl, NO_DEMO_PLACE, withDemoQuery } from "./stateForms";
import type { DemoPlace } from "./stateForms";
import { useStateRules, type StateRulesView } from "./stateRules";
import { demoChanges, everyState, type DemoType } from "./demo";
import { everyBoxTicked } from "./minors";
import { cosmeticOffer, servicesOf, shopType } from "./service";
import { protectionsOff } from "./protections";
import { currentSubdomain, isDemoShop } from "./subdomain";
import { loadQrModules, qrPath } from "./qr";
import { announcePair, mintPairId, pairIdFrom, pollForPhone, withPairId, withoutPairId } from "./pairing";
import type { PairingPoll } from "./pairing";
import QrPicker from "./QrPicker";
import TextsPage from "./TextsPage";
import { isTextsPage, TEXTS_WORDS } from "./texts";
import { depositPageUrl, nextPickerState, offersPicker } from "./picker";
import { conventionOf } from "./convention";
import type { PickerState } from "./picker";


// Where the wordmark on the demo's chrome goes: the front of the business, which is what a shop
// owner who likes what they are looking at wants next.
const MARKETING_URL = "https://www.waivers.tattoo/";
// (the demo chrome's two wordmark links leave through carry.ts siteHref, with what brought the visitor: 2026-09-30)

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <BasePage>
        <Form />
      </BasePage>
    ),
    errorElement: <RouteCrashScreen />,
  },
  // Terms and conditions:
  {
    path: "/terms-and-conditions",
    element: <TermsAndConditions />,
    errorElement: <RouteCrashScreen />,
  },
  // Waiver detail:
  {
    path: "/w/:waiver_unique_id",
    element: <WaiverDetail />,
    errorElement: <RouteCrashScreen />,
  },
  // 404:
  {
    path: "*",
    element: <PageNotFound />,
    errorElement: <RouteCrashScreen />,
  },
]);

/** A shop's ticked boxes as the row keeps them ({id: true}), the literal trues only; anything that is not a map is none. */
function ticksOf(raw: unknown): Record<string, boolean> | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const out: Record<string, boolean> = {};
  for (const [id, flag] of Object.entries(raw as Record<string, unknown>)) if (flag === true) out[id] = true;
  return out;
}

class ClientPublicData {
  logo_url?: string;

  include_needle_and_ink_batch_number_pictures?: boolean;
  /** 2026-10-31 — which of the two lot photos the form will not post without, while the ask above is on (lotPhotos.ts):
   *  {needle, ink}, each true or false; absent is neither (a shop from before the switch keeps its photos optional). */
  lot_photos_required?: { needle?: boolean; ink?: boolean };
  ask_more_questions_about_medications_and_blood_thinners?: boolean;
  /** TW-0059 — `tattoo` · `piercing` · `both`. Absent means tattoo, which is what every shop that
   *  existed before 2026-09-05 sends; service.ts is the only place that decides what it means. */
  shop_type?: string;
  /** TW-0279 — cosmetic tattooing, the third service: "also" (beside what shop_type says), "only" (a cosmetic studio), or
   *  absent for a shop that does none, which is every shop until the shop app and the site can write it
   *  (docs/COSMETIC.md's build order: readers before writers). service.ts `servicesFor` is the only place that decides
   *  what it means. Named here or the form never sees it: this class keeps only the keys it names. */
  cosmetic?: string;
  /** TW-0279 — the owner's word that its county board allows powder, ombré and combination brows under Georgia's eye-area
   *  rule (cosmetic.ts cosmeticBarsFor reads it through the contract's `unless`): a literal true only, as the API reads
   *  it, and nothing writes it yet. Named here or the form never sees it. */
  cosmetic_shaded_brows?: boolean;
  /** TW-0279 slice 7 — the owner's word that its studio works under the direction of a physician who is on site, which lifts
   *  South Carolina's head, face and neck bar (cosmetic.ts cosmeticBarsFor reads it through the contract's `unless`, and
   *  cosmeticLiftsFor says why under the picker): a literal true only, as the API reads it. Named here or the form never
   *  sees it. */
  cosmetic_physician_directed?: boolean;
  /** TW-0279 slice 10 — does the cosmetic form ask for a photo of the client's doctor's written instructions on areola,
   *  camouflage and scar-covering scalp work? The owner's own switch (Settings → Waiver form), off until they turn it on;
   *  the shop's state's rule asks regardless where its record names it (New Jersey's; cosmetic.ts asksProviderInstructions).
   *  A literal true only. Named here or the form never sees it: this class keeps only the keys it names. */
  ask_provider_instructions_photo?: boolean;
  /** The shop's own artists, when it keeps a list (docs/APP.md → Artists). `[{id, name}]`, with
   *  `license_number` where the shop filled one and, since 2026-09-14, `does` (`["tattoo"]`,
   *  `["piercing"]`) where the shop listed an artist on one form only; absent or empty means this
   *  shop's client types a name, the way every shop's client always has. artists.ts is the only
   *  place that decides what it means. */
  artists?: { id?: string; name?: string; license_number?: string; does?: string[] }[];
  /** 2026-09-14 — the one material a shop uses, when it uses only one (Settings → Waiver form →
   *  Jewelry; 2 Ton Gallery's ask). "" or absent: the client is asked, as every piercing form has
   *  done since TW-0059. service.ts `fixedJewelryMaterial` is the only reader. */
  jewelry_material?: string;
  /** TW-0422 (2026-10-22) — the piercings the shop does (Settings → Waiver form → Piercings you do): the spots' values, absent
   *  for every spot, which is every shop that never said. service.ts `piercingSpotsFor` is the only reader. Named here or the
   *  form never sees it: this class keeps only the keys it names. */
  piercing_spots?: string[];
  /** TW-0422 — Ask which artist (a tattoo's page) and Ask which piercer (a piercing's): the owner's switch, one per service.
   *  Absent or true is the question every form has asked; only a literal false takes it off that page, so nothing else is
   *  kept. artists.ts `asksArtistFor` is the only reader, and the state's rule wins there. */
  ask_artist_tattoo?: boolean;
  ask_artist_piercing?: boolean;
  /** The languages the shop offers on its form (i18n.ts enabledLangs): absent = English only, no
   *  switch. The owner turns Spanish on in the shop app's Settings. */
  languages?: string[];
  /** TW-0128 — does this shop pierce minors with a parent present? OFF for every shop that has not
   *  said so, which is every shop that predates the setting. It is only the FIRST of three gates:
   *  the state's own rule and the placement decide the rest (minors.ts). */
  pierce_minors?: boolean;
  /** TW-0150 — and does it TATTOO one? A separate switch from the piercing above, because it is a
   *  separate law: 33 states hand the parent a route and 18 make it a crime whatever the parent
   *  signs, and Oregon and Washington are a yes for the piercing and a no for this. */
  tattoo_minors?: boolean;
  /** 2026-11-03 — the boxes the shop ticked under each minors switch (Settings → Who can sign), each a literal true: a paper
   *  that waits on its box (New York's and Wisconsin's state form) is asked of a parent only once the box is ticked
   *  (minors.ts papersAsked, minorsTicked). Named here or the form never sees them: this class keeps only the keys it names. */
  pierce_minors_ack?: Record<string, boolean>;
  tattoo_minors_ack?: Record<string, boolean>;
  /** TW-0154 — does this shop want the price of the work on the waiver? OFF for every shop that has
   *  not asked for it, which is every shop that predates the setting; the owner turns it on in the
   *  shop app's Settings → Waiver form. Even ON it is an optional box: a client may not know the
   *  number, and a required money box on a consent form is a signature lost. The LABEL follows the
   *  service, not this key — `shop_type` can be piercing or both (service.ts, i18n.ts priceLabel). */
  ask_price?: boolean;
  /** 2026-09-23 — does this shop ask "How are you paying today?" (Cash · Card · Other)? Cue at Piercinks (Miami):
   *  "is there a way to add a line for a form of payment?" OFF for every shop that has not turned it on in the shop
   *  app's Settings → Waiver form → Price, and only a literal true counts. Unlike the price, a client must pick one
   *  before signing once the shop asks (Form.tsx; payment.ts asksPaymentMethod is the one reading). Named here or
   *  the form never sees it: this class keeps only the keys it names. */
  ask_payment_method?: boolean;
  /** 2026-09-11 — does this shop ask its client who to call? OFF for every shop that has not turned it
   *  on. It is only the shop's half: a state whose rule wants an emergency contact on the record (South
   *  Carolina, New Jersey) turns the section on whatever this says (Form.tsx, `settingForcedBy`). */
  ask_emergency_contact?: boolean;
  /** TW-0293 (2026-10-15) — the two questions every form asked since 2023, which follow the shop's state now (David
   *  McFarland, Ink Slingerz Tattoo Studio, Oklahoma: "Is there a way to eliminate some of the pre-loaded questions that
   *  are on the waiver form that are unnecessary by Oklahoma regulations?"). Each is the shop's own half, a literal true
   *  only; a state whose record names the question keeps it on whatever this says, and a shop with no state is asked
   *  both (defaultQuestions.ts is the one reading). Named here or the form never sees them. */
  ask_drugs_or_alcohol?: boolean;
  ask_communicable_disease?: boolean;
  /** 2026-09-25 — does this shop ask a client who their doctor is, on a tattoo and/or on a piercing? Ana Ruiz at
   *  Squirtin' Ink: "For the piercing forms can we add a physician name , number and address." ONE SWITCH PER
   *  SERVICE, off for every shop that has not turned it on in Settings → Waiver form → Physician; a literal true
   *  only (physician.ts asksPhysician is the one reading). No state forces it. Named here or the form never sees
   *  them: this class keeps only the keys it names. */
  ask_physician_tattoo?: boolean;
  ask_physician_piercing?: boolean;
  /** TW-0171 — may a client choose a SAVED photo of their ID ("Choose from library") instead of taking
   *  one? THREE states, on purpose: true or false is the owner's own word from Settings → Waiver form (beside
   *  the sentence saying a saved photo is a copy for the file and never proof of age), and ABSENT means the
   *  form starts it ON (stateForms.ts idPhotoLibraryStartsOn: a saved photo is the copy every state's rule
   *  takes, the camera's equal; Chris, 2026-10-01: "i think we should have it on by default for states that
   *  support it." · "it sounds like the 21 can be expanded if it's allowed?"). Anything that is not a boolean
   *  is no word. From 2026-09-11 to then only a literal true counted ("we don't auto-opt states in"). */
  id_photo_from_library?: boolean;
  /** TW-0195 — does this shop offer "Been here before?" at the top of its form, the short panel that
   *  asks for a phone and an email and fills in the contact block from the client's last waiver at
   *  this shop? OFF for every shop that has not turned it on in Settings → Waiver form, which is
   *  every shop that predates the setting (Chris, 2026-09-21: "the form works so well already that
   *  tacking more onto it seems ... risky in some respects"). `offersReturningLookup` in
   *  recognition.ts is the only place that decides what it means. Named here or the form never sees
   *  it: this class keeps only the keys it names. */
  returning_lookup?: boolean;
  /** 2026-09-24 — does the form ask for a photo of the birth certificate or guardianship papers when a parent
   *  or guardian signs for a minor? The owner's own switch (Settings → Who can sign); the state's rule turns it
   *  on for a service regardless (minors.ts asksGuardianProof). A literal true only. Named here or the form
   *  never sees it: this class keeps only the keys it names. */
  ask_guardian_proof_photo?: boolean;
  /** 2026-10-08 — does the form ask, under a minor's ear spot, whether the piercing is done with a single-use
   *  stud-and-clasp gun? The owner's own switch (Settings → Who can sign → Ear piercing with a stud gun), off until they
   *  turn it on: a shop that pierces with needles never sees the question and the state's whole minors rule applies
   *  (minors.ts ruleForShop). A literal true only. Named here or the form never sees it: this class keeps only the keys
   *  it names. */
  ask_stud_gun?: boolean;
  /** TW-0628 (2026-11-04) — the protections this shop's owner turned off with the risk accepted (Settings → Waiver form →
   *  Protections): {id: the product's day it went off}, the ids the page knows (protections.ts is the one reading). Absent on
   *  every shop that never turned one off, which is every shop that existed the day it shipped: every protection on, the form as
   *  it always was. Named here or the form never sees it: this class keeps only the keys it names. */
  protections_off?: Record<string, string>;
  // 2026-09-11 — how the client enters the price (price.ts): "choices", the default and what an absent key reads as,
  // or "free", and the shop's own hourly rate for the hourly choice. Named here or the form never sees them: this
  // class keeps only the keys it names, which is how both went missing on the first try.
  price_style?: "choices" | "free";
  hourly_rate?: string;
  /** 2026-09-17 — the shop's county board of health, name and phone in the owner's own words ("Cobb Environmental
   *  Health, (770) 435-7815"), which Georgia's rule prints on the consent with how to reach it (r. 511-3-8-.08(2)(f);
   *  stateForms.ts `noticeText` fills `{health_authority}` from this). "" or absent: the consent says "your county
   *  board of health" until the owner fills it in Settings → Waiver form. Named here or the form never sees it. */
  health_authority?: string;

  /** 2026-09-24 — the shop's own clauses, `[{id, text}]` in the owner's words (Settings → Waiver form → Your own
   *  clauses; Shop 53 Custom Tattoo's ask), each a box the client ticks before signing, printed on the signed
   *  waiver from the row by id. Absent or empty: the form every shop has had. ownClauses.ts is the only reader.
   *  Named here or the form never sees it: this class keeps only the keys it names. */
  own_clauses?: { id?: string; text?: string }[];

  /** TW-0412 — the shop's own questions, `[{id, text, when, note}]` in the owner's words (Settings → Waiver form → Your own
   *  questions; Perfecto Bliss's ask), each a Yes / No the client answers among the health questions, with the owner's line
   *  under the answer `when` names. Absent or empty: the form every shop has had. ownQuestions.ts is the only reader. Named
   *  here or the form never sees it: this class keeps only the keys it names. */
  own_questions?: { id?: string; text?: string; when?: string | null; note?: string }[];

  /** 2026-10-02 — cash preferred, the card-fee box (Reckless Heart Tattoo's ask): the map the API's settings door
   *  wrote (the percent, the owner's words, the id, and the two sentences the client reads), or absent/false for the
   *  form every shop has had. cashPreferred.ts is the only reader. Named here or the form never sees it. */
  cash_preferred?: Record<string, unknown>;

  /** 2026-10-08 — a shop in HIPAA mode's two form settings (hipaaForm.ts is the only reader): the practice's notice of privacy
   *  practices ({title, text, version}) and the photo release ({years}). The API writes them only for a shop in HIPAA mode,
   *  and the form draws them only when `/shop` says the shop is in it. Named here or the form never sees them. */
  privacy_notice?: Record<string, unknown>;
  photo_release?: Record<string, unknown>;

  /** TW-0206 — WHAT THE PRINTED QR CODE OPENS. `qr_picker` is the owner's switch (the shop app's Settings → Shop
   *  details → Your QR code) and `deposits_on` the deposit page's own answer as the shop app carried it; with both a
   *  literal true this page opens on a short choice first, Sign your waiver · Pay a deposit (picker.ts offersPicker,
   *  QrPicker.tsx). Absent on every shop that never turned it on: the form, as it always was. Named here or the form
   *  never sees them: this class keeps only the keys it names. */
  qr_picker?: boolean;
  deposits_on?: boolean;

  constructor(data: any) {
    this.logo_url = data.logo_url;
    this.include_needle_and_ink_batch_number_pictures = data.include_needle_and_ink_batch_number_pictures;
    this.lot_photos_required = data.lot_photos_required;
    this.ask_more_questions_about_medications_and_blood_thinners = data.ask_more_questions_about_medications_and_blood_thinners;
    this.shop_type = data.shop_type;
    this.cosmetic = typeof data.cosmetic === "string" ? data.cosmetic : undefined;
    this.cosmetic_shaded_brows = data.cosmetic_shaded_brows === true;
    this.cosmetic_physician_directed = data.cosmetic_physician_directed === true;
    this.ask_provider_instructions_photo = data.ask_provider_instructions_photo === true;
    this.artists = data.artists;
    this.languages = Array.isArray(data.languages) ? data.languages.map((v: unknown) => `${v}`) : undefined;
    this.pierce_minors = Boolean(data.pierce_minors);
    this.tattoo_minors = Boolean(data.tattoo_minors);
    this.pierce_minors_ack = ticksOf(data.pierce_minors_ack);
    this.tattoo_minors_ack = ticksOf(data.tattoo_minors_ack);
    this.ask_price = Boolean(data.ask_price);
    this.ask_payment_method = data.ask_payment_method === true;
    this.ask_emergency_contact = Boolean(data.ask_emergency_contact);
    this.ask_drugs_or_alcohol = data.ask_drugs_or_alcohol === true;
    this.ask_communicable_disease = data.ask_communicable_disease === true;
    this.ask_physician_tattoo = data.ask_physician_tattoo === true;
    this.ask_physician_piercing = data.ask_physician_piercing === true;
    this.id_photo_from_library = typeof data.id_photo_from_library === "boolean" ? data.id_photo_from_library : undefined;
    this.returning_lookup = data.returning_lookup === true;
    this.ask_guardian_proof_photo = data.ask_guardian_proof_photo === true;
    this.ask_stud_gun = data.ask_stud_gun === true;
    this.protections_off = protectionsOff(data.protections_off);
    this.price_style = data.price_style === "free" ? "free" : "choices";
    this.hourly_rate = `${data.hourly_rate ?? ""}`.trim();
    this.jewelry_material = `${data.jewelry_material ?? ""}`.trim();
    this.piercing_spots = Array.isArray(data.piercing_spots) ? data.piercing_spots.filter((v: unknown) => typeof v === "string") : undefined;
    this.ask_artist_tattoo = data.ask_artist_tattoo === false ? false : undefined;
    this.ask_artist_piercing = data.ask_artist_piercing === false ? false : undefined;
    this.health_authority = `${data.health_authority ?? ""}`.trim();
    this.own_clauses = Array.isArray(data.own_clauses) ? data.own_clauses : undefined;
    this.own_questions = Array.isArray(data.own_questions) ? data.own_questions : undefined;
    this.cash_preferred = data.cash_preferred && typeof data.cash_preferred === "object" && !Array.isArray(data.cash_preferred) ? data.cash_preferred : undefined;
    this.privacy_notice = data.privacy_notice && typeof data.privacy_notice === "object" && !Array.isArray(data.privacy_notice) ? data.privacy_notice : undefined;
    this.photo_release = data.photo_release && typeof data.photo_release === "object" && !Array.isArray(data.photo_release) ? data.photo_release : undefined;
    this.qr_picker = data.qr_picker === true;
    this.deposits_on = data.deposits_on === true;
  }
}

export class Client {
  name: string;
  subdomain: string;
  public_data?: ClientPublicData;
  /** The trial: the API says when the free waivers are used up, and where the shop activates. Every
   *  shop carries these since TW-0067 (every shop is a row); an unmetered one is never locked. */
  locked?: boolean;
  /** TW-0158 — past the free ten and still signing. The form RUNS and wears a banner; `locked` is
   *  the wall at twenty and is a different fact. Any unmetered shop (legacy, active, comped) is
   *  false on both. */
  over_trial?: boolean;
  activate_url?: string;
  checkout_url?: string;
  trial_waivers_left?: number | null;
  lock_waivers?: number;
  /** Neither source has answered yet: the form is painted, the shop's name and mark are on the way. */
  placeholder?: boolean;
  /** TW-0500 — this is the static file's answer and the API's has not landed yet: the shop as the CDN last cached it, which can
   *  be minutes behind the row. The form puts a draft back only against the API's answer (Form.tsx, draft.ts). */
  fromFile?: boolean;
  /** 2026-10-08 — the shop is in HIPAA mode (docs/HIPAA.md): `/shop` says `hipaa: true`, and only then (the static config does
   *  not carry it). hipaaForm.ts reads it: the notice and the photo release are drawn, and the portfolio clause is not. */
  hipaa?: boolean;
  /** The shop's state, 2 letters, from its row — the form asks what that state's rule requires
   *  (stateForms.ts, TW-0104). Absent on the six shops in code and on a row with no address. */
  state?: string | null;
  /** TW-0153 — and WHICH JURISDICTION inside it, because six are stricter than their state:
   *  Baltimore City bans a minor's tattoo outright where Maryland allows one, Clark County and
   *  Philadelphia set floor ages their states do not. `county` is Places' administrative_area_level_2
   *  where there is one and the LOCALITY where there is not (an independent city belongs to no
   *  county), and `countyKind` says which — the rule matches exactly within a kind and never by
   *  trimming " County" off a name. Absent answers exactly as the state does. */
  county?: string | null;
  countyKind?: string | null;
  /** TW-0227 — and the CITY inside the county, Google's locality exactly as Google writes it ("Hammond"), because a
   *  city inside a county can be stricter than both: Hammond and East Chicago want both parents' written permission
   *  for a minor where Lake County asks nothing. Absent answers as the county or the state does. */
  city?: string | null;
  /** TW-0225 — the demo's fifth dial (`?ear=1`): the demo client is here for an ear piercing, so the form starts a
   *  piercing's spot at Earlobe. Set only by `withDemo`, never from a shop's data: no real shop's form fills a spot. */
  demoEar?: boolean;
  /** 2026-10-18 — A TATTOO CONVENTION (docs/CONVENTIONS.md): the show this page is, {name, from, to}, which turns the artist
   *  field into a search over the show's roster (convention.ts, ArtistSearch.tsx). Only the API's `/shop` carries it, and
   *  only while the API's FEATURE_CONVENTIONS is on; the static config never does. Absent on every shop. */
  convention?: { name: string; from: string; to: string } | null;
  /** TW-0517 (2026-10-27) — A COLD PAGE NOBODY HAS CLAIMED: the API's `/shop` says `cold: true` for a page minted the day its cold
   *  placard was ordered and not claimed yet (common.unclaimed_cold), with `claim_url`, /start told which page and carrying the
   *  card's tags. The banner at the top and the two doors at 10 and 20 read them. Absent on every other shop. */
  cold?: boolean;
  claim_url?: string;

  constructor(data: any) {
    this.name = data.name;
    this.subdomain = data.subdomain;
    this.state = data.state ? `${data.state}`.trim().toUpperCase() : null;
    this.county = data.county ? `${data.county}`.trim() : null;
    this.countyKind = data.county_kind ? `${data.county_kind}`.trim().toLowerCase() : null;
    this.city = data.city ? `${data.city}`.trim() : null;
    this.public_data = new ClientPublicData(data.public_data || {});
    this.locked = Boolean(data.locked);
    this.over_trial = Boolean(data.over_trial);
    this.activate_url = data.activate_url;
    this.checkout_url = data.checkout_url;
    this.trial_waivers_left = data.trial_waivers_left;
    this.lock_waivers = typeof data.lock_waivers === "number" ? data.lock_waivers : undefined;
    this.placeholder = Boolean(data.placeholder);
    this.fromFile = data.fromFile === true;
    this.hipaa = data.hipaa === true;
    this.convention = conventionOf(data);
    this.cold = data.cold === true;
    this.claim_url = typeof data.claim_url === "string" && /^https:\/\/www\.waivers\.tattoo\/start\?/.test(data.claim_url) ? data.claim_url : undefined;
  }

  /** The same shop with another state, kind, minors setting and proof-of-guardianship switch — the demo
   *  shop's dials (and `?state=`, `?type=`, `?minors=`, `?proof=`, `?ear=`), never a real shop's: a real shop's are
   *  set once, in its settings. */
  withDemo(state: string | null, type: string | null, minors: boolean = false, county: string | null = null, countyKind: string | null = null, proof: boolean = false, city: string | null = null, ear: boolean = false): Client {
    const next = new Client({
      ...this,
      public_data: {
        ...(this.public_data ?? {}),
        // TW-0279 — `?type=cosmetic` is a cosmetic studio: its own key, "only", and the shop_type left alone, the way a
        // real row says it (service.ts servicesFor)
        ...(type === "cosmetic" ? { cosmetic: "only" } : type ? { shop_type: type } : {}),
        // 2026-11-03 — and every box its state asks ticked, as a real shop's are once its switch goes on (TW-0153): a paper that
        // waits on its box (New York's and Wisconsin's state form) is asked on the demo as at a new shop's counter
        ...(minors
          ? { pierce_minors: true, tattoo_minors: true, pierce_minors_ack: everyBoxTicked("piercing", state), tattoo_minors_ack: everyBoxTicked("tattoo", state) }
          : {}),
        // 2026-09-24 — the fourth dial, the shop's own switch on a real row (minors.ts asksGuardianProof)
        ...(proof ? { ask_guardian_proof_photo: true } : {}),
        // 2026-10-08 — the ear dial turns the stud-gun switch on with it, so a demo link that asks for a minor's ear
        // piercing in a stud-gun state asks its question; a real shop's switch is off until its owner turns it on
        ...(ear ? { ask_stud_gun: true } : {}),
      },
    });
    next.state = state ? `${state}`.trim().toUpperCase() : null;
    // the demo's county goes with its state and is cleared when the state changes: a leftover
    // "Baltimore" under a Nevada demo would be a jurisdiction that does not exist
    next.county = county ? `${county}`.trim() : null;
    next.countyKind = countyKind ? `${countyKind}`.trim().toLowerCase() : null;
    // TW-0227 — the demo's city goes with its county, for the same reason (`?county=Lake%20County&city=Hammond`)
    next.city = county && city ? `${city}`.trim() : null;
    // TW-0225 — the fifth dial is about the demo's CLIENT, not a switch on the shop's row, so it rides the object and
    // not public_data: a minor's ear piercing, whose spot the form starts at Earlobe
    next.demoEar = ear;
    return next;
  }
}


// The shop's public config as a static file on the CDN, the form's fast path (measured 2026-09-05:
// the API's /shop shares a Lambda with the PDF renderer and cold-starts at 1.5–2 s; this file is
// ~30 ms from the edge). Relative first — the same origin on the wildcard CDN, no second
// connection. The six shops' forms still come from the old Amplify app, which answers any unknown
// path with index.html (its SPA rewrite), so a non-JSON answer means "no file here" and the
// absolute host is tried next (CORS on that path: infrastructure/prod/form.tf). Written by the
// site at signup and logo change, regenerated by `./run.sh sync-shop-configs`.
const SHOP_CONFIG_HOST = "https://shops.waivers.tattoo";

async function fetchShopConfigFile(subdomain: string): Promise<any | null> {
  for (const url of [`/_shops/${subdomain}.json`, `${SHOP_CONFIG_HOST}/_shops/${subdomain}.json`]) {
    try {
      const res = await fetch(url);
      if (!res.ok) continue;
      if (!(res.headers.get("content-type") || "").includes("application/json")) continue;
      const data = await res.json();
      if (data && data.subdomain === subdomain && data.name) return data;
    } catch {
      // the next candidate, then the API
    }
  }
  return null;
}

/** THE LINE ABOVE A RUNNING FORM once the free ten are spent (TW-0158, 2026-09-10). Chris:
 *  "it would be embarrasing to show a blockage so maybe it's a banner at the top of the signing
 *  experience that they casually show so it's not blocking the owner/artist?"
 *
 *  IT IS READ BY A STRANGER. The form is on the CLIENT's phone — someone who scanned a QR code at a
 *  counter to get tattooed — so it opens by saying who it is for and never addresses them. Nothing
 *  here blocks, dims, or scrolls the form; it is one line the client's eye slides off and the shop's
 *  does not. It is the last chance to be quiet: the next state is the wall.
 *
 *  No dismiss button, deliberately. A client dismissing a shop's notice on their own phone is a
 *  press that means nothing (the next client sees it again), and it would put a control on a
 *  stranger's screen for a decision that is not theirs. */
function TrialBanner({ client }: { client: Client }) {
  // same language rules as the rest of the form: the shop's enabled set, then the phone, then ?lang=
  const T = strings(detectLang(undefined, undefined, enabledLangs(client.public_data)));
  const href = client.checkout_url || client.activate_url;
  // `py-2 -my-2` on the link grows its TAP TARGET without growing the banner: measured at 390x844
  // it goes from 15px tall to about 31px while the banner stays 2 lines and 49px. 15px is below
  // what a thumb lands on reliably. Deliberately NOT a 44px button — this is a line a client's eye
  // should slide off, and a big target on a stranger's phone invites an accidental tap through to
  // a Stripe checkout, which is a worse outcome than an owner having to aim.
  // TW-0517: a cold page nobody has claimed carries the claim beside the payment, in the same one line (the claim gives the page an
  // owner; the payment does too, with the email Stripe takes, and unlocks it)
  if (client.cold && client.claim_url) {
    return (
      <div className="bg-gray-100 border-b border-gray-300 text-gray-800 text-xs px-4 py-2 text-center">
        <span>{T.overTrialNote(client.lock_waivers ?? 20)}</span>{" "}
        <a href={client.claim_url} className="underline font-semibold whitespace-nowrap inline-block py-2 -my-2">
          {T.coldAddEmail}
        </a>
        {href ? (
          <>
            {" · "}
            <a href={href} className="underline font-semibold whitespace-nowrap inline-block py-2 -my-2">
              {T.coldPay(`$${pricing.yearly}`)}
            </a>
          </>
        ) : null}
      </div>
    );
  }
  return (
    <div className="bg-gray-100 border-b border-gray-300 text-gray-800 text-xs px-4 py-2 text-center">
      <span>{T.overTrialNote(client.lock_waivers ?? 20)}</span>{" "}
      {href ? (
        <a href={href} className="underline font-semibold whitespace-nowrap inline-block py-2 -my-2">
          {T.overTrialAction(`$${pricing.yearly}`)}
        </a>
      ) : null}
    </div>
  );
}

/** TW-0517 (2026-10-27) — THE TOP OF A COLD PAGE NOBODY HAS CLAIMED: the page was minted for the shop the day its cold placard was
 *  ordered, and it is live. One line naming it, the owner's one door (the claim: /start, the email the account is, the shop filled
 *  in) and a dismiss that hides it on this phone alone (localStorage, per page). It comes back at the tenth signing, where the trial
 *  banner's line carries the claim and the payment. Read by a client too, so it says what the page is and asks nothing of them. */
export const COLD_BANNER_DISMISSED = "wt_cold_banner_dismissed";

function ColdBanner({ client }: { client: Client }) {
  const T = strings(detectLang(undefined, undefined, enabledLangs(client.public_data)));
  const key = `${COLD_BANNER_DISMISSED}:${client.subdomain}`;
  const [dismissed, setDismissed] = React.useState<boolean>(() => {
    try {
      return window.localStorage.getItem(key) === "1";
    } catch {
      return false;
    }
  });
  if (dismissed || !client.claim_url) return null;
  const dismiss = () => {
    try {
      window.localStorage.setItem(key, "1");
    } catch {
      /* a phone with no storage keeps the banner, which is the safe side */
    }
    setDismissed(true);
  };
  return (
    <div className="bg-gray-900 text-white text-sm px-4 py-2 flex items-center justify-center gap-3" role="region" aria-label={T.coldBanner(client.name)}>
      <span>{T.coldBanner(client.name)}</span>
      <a href={client.claim_url} className="underline font-semibold whitespace-nowrap inline-block py-2 -my-2">
        {T.coldAddEmail}
      </a>
      {/* the same screen: its logo comes first there, and needs no email */}
      <a href={client.claim_url} className="underline whitespace-nowrap inline-block py-2 -my-2">
        {T.coldAddLogo}
      </a>
      <button type="button" onClick={dismiss} className="underline whitespace-nowrap py-2 -my-2" aria-label={T.coldDismiss}>
        {T.coldDismiss}
      </button>
    </div>
  );
}

/** What a client sees when the shop's free waivers are used up: the shop, not the client, has
 *  something to do, and it takes about a minute on whichever phone is holding this page. */
function LockedScreen({ client }: { client: Client }) {
  // this screen has no switch of its own: the phone's language, or ?lang= (i18n.ts)
  const T = strings(detectLang());
  return (
    <div className="flex flex-col justify-center items-center min-h-screen p-8 text-center">
      <div className="max-w-md">
        {client.public_data?.logo_url ? (
          <img src={client.public_data.logo_url} alt="" className="mx-auto mb-6 max-h-24" />
        ) : null}
        <h1 className="text-2xl font-bold mb-4">{T.lockedTitle(client.name)}</h1>
        <p className="mb-6">{T.lockedBody(`$${pricing.yearly}`)}</p>
        {client.cold && client.claim_url ? (
          // TW-0517: a cold page nobody has claimed: the same screen, two doors. The payment unlocks it and makes it the payer's (the
          // webhook claims it with the email Stripe takes); the claim gives it an owner, who then activates it from the dashboard.
          // No top-up here: it tells the owner who asked, and a cold page has nobody to tell.
          <div className="flex flex-col items-center gap-3">
            <a href={client.checkout_url || client.activate_url} className="inline-block bg-black text-white px-6 py-3 rounded font-semibold">
              {T.coldPay(`$${pricing.yearly}`)}
            </a>
            <a href={client.claim_url} className="inline-block border border-black px-6 py-3 rounded font-semibold">
              {T.coldSetUp}
            </a>
          </div>
        ) : (
          <>
            {client.activate_url ? (
              <a href={client.activate_url} className="inline-block bg-black text-white px-6 py-3 rounded font-semibold">
                {T.activate(client.name)}
              </a>
            ) : null}
            <TopUpOffer subdomain={client.subdomain} T={T} />
          </>
        )}
        <p className="mt-6 text-sm text-gray-600">{T.notTheOwner}</p>
      </div>
    </div>
  );
}

/** THE SECOND PERSON'S WAY BACK IN (TW-0286; topUp.ts has the rule's half this screen holds). One quieter line and one
 *  email box under Activate, drawn only when the promotions service says this shop can have it, so the paused screen of a
 *  shop it does not open for is byte for byte what it was. Activate stays the first thing and the black button: nothing
 *  here lowers the price or stands in front of it. A yes reloads the page into the working form; a no says why in one
 *  sentence and keeps the box. The box takes the browser's own checks first (an email box, required). */
function TopUpOffer({ subdomain, T }: { subdomain: string; T: Strings }) {
  const [open, setOpen] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [said, setSaid] = React.useState<string | null>(null);

  useEffect(() => {
    let live = true;
    topUpOpen(subdomain).then((yes) => {
      if (live) setOpen(yes);
    });
    return () => {
      live = false;
    };
  }, [subdomain]);

  if (!open) return null;

  const refusal = (code: string): string => {
    if (code === "owner_email") return T.topUpOwnerEmail;
    if (code === "already_topped_up") return T.topUpAlready(TOP_UP_WAIVERS);
    if (code === "bad_email") return T.topUpBadEmail;
    if (code === "not_open" || code === "unknown_shop") return T.topUpNotOpen;
    return T.topUpTryAgain;
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setSaid(null);
    const answer = await askTopUp(subdomain, email.trim());
    if (worksAgain(answer)) {
      topUpPage.reload();
      return;
    }
    setBusy(false);
    setSaid(answer.ok ? null : refusal(answer.code));
  };

  return (
    <form className="mt-6 text-sm" onSubmit={submit}>
      <p className="text-gray-700 mb-2">{T.topUpLine(TOP_UP_WAIVERS)}</p>
      <label htmlFor="top-up-email" className="sr-only">
        {T.topUpEmailLabel}
      </label>
      <div className="flex gap-2">
        <input
          id="top-up-email"
          type="email"
          required
          autoComplete="email"
          inputMode="email"
          placeholder={T.topUpEmailLabel}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1 min-w-0 border border-gray-300 rounded px-3 py-2"
        />
        <button type="submit" disabled={busy} className="border border-black rounded px-3 py-2 font-semibold whitespace-nowrap">
          {busy ? T.topUpBusy(TOP_UP_WAIVERS) : T.topUpButton(TOP_UP_WAIVERS)}
        </button>
      </div>
      <p className="mt-2 text-gray-600">{T.topUpNote}</p>
      {said ? (
        <p role="alert" className="mt-2 text-gray-900">
          {said}
        </p>
      ) : null}
    </form>
  );
}

// "closed" (TW-0593, 2026-11-02): the shop closed its account with us, every record kept, and the API's /shop says so with a
// 410 (consolidated_api/app.py retrieve_shop_details) where every other no-shop is the 400 a stranger's address gets
type ShopLoadError = "no-shop" | "closed" | "load-failed" | null;

/** What a person sees when the address they opened has no shop behind it, or ours did not answer
 *  (TW-0097). The wildcard CDN serves this bundle at every *.waivers.tattoo, so a retired, renamed
 *  or hand-typed subdomain reaches a real page with nothing to put on it. Until 2026-09-08 that
 *  rendered the raw string "No client found (bad response)" — a note to whoever wrote the fetch,
 *  on the screen of someone standing at a counter whose day has already gone wrong.
 *
 *  It takes the PHONE's language, not the shop's, and deliberately: enabledLangs gates Spanish on
 *  a shop's own setting, and here there is no shop to have one, so the only signal left is the
 *  phone. LockedScreen resolves its language the same way. */
function ShopLoadErrorScreen({ kind }: { kind: Exclude<ShopLoadError, null> }) {
  const T = strings(detectLang());
  const title = kind === "no-shop" ? T.noShopTitle : kind === "closed" ? T.closedTitle : T.loadFailedTitle;
  const body = kind === "no-shop" ? T.noShopBody : kind === "closed" ? T.closedBody : T.loadFailedBody;
  return (
    <div className="flex flex-col justify-center items-center min-h-screen p-8 text-center">
      <div className="max-w-md">
        <h1 className="text-2xl font-bold mb-4">{title}</h1>
        <p className="mb-6">{body}</p>
        <p className="text-sm text-gray-600">waivers.tattoo</p>
      </div>
    </div>
  );
}

/** The words a client gets when our code breaks under them, shared by the two places that catch a
 *  crash. It takes the SHOP's languages: there is a shop here, and one with Spanish off should not
 *  start speaking Spanish because the page broke. */
function CrashScreen({ client }: { client: Client | null }) {
  const T = strings(detectLang(undefined, undefined, enabledLangs(client?.public_data)));
  return (
    <div className="flex flex-col justify-center items-center min-h-screen p-8 text-center">
      <div className="max-w-md">
        <h1 className="text-2xl font-bold mb-4">{T.crashTitle}</h1>
        <p className="mb-6">{T.crashBody}</p>
        <p className="text-sm text-gray-600">waivers.tattoo</p>
      </div>
    </div>
  );
}

/** ⚠ THE ROUTER CATCHES A ROUTE'S CRASH BEFORE ANY BOUNDARY OF OURS CAN (TW-0132, measured
 *  2026-09-09 while writing the test for the boundary below — the boundary never fired).
 *  `createBrowserRouter` gives every route a DEFAULT `errorElement`, so a component that throws is
 *  handled inside react-router and never re-thrown: what a client got was react-router's own
 *  developer page, "💿 Hey developer 👋 · You can provide a way better UX than this…", on their
 *  phone at a counter. There is no opting out of that default; the only lever is to set our own,
 *  which is why every route above names this one.
 *
 *  This is where a crash on `/w/:waiver_unique_id` — the one Chris found — actually lands. */
function RouteCrashScreen() {
  const error = useRouteError();
  const { client } = useApp();
  // one crash, one row: StrictMode runs an effect twice in a DEV build (never in the deployed one),
  // and a ref survives that because it is the same instance both times
  const reported = React.useRef(false);
  React.useEffect(() => {
    if (reported.current) return;
    reported.current = true;
    // TW-0500 — the form's own crash lands here first (the route's error element), so the draft goes here too (draft.ts)
    forgetEveryDraft();
    const e = error as { message?: unknown; stack?: unknown } | undefined;
    trackError(typeof e?.message === "string" && e.message ? e.message : "route_error", {
      source: "route",
      stack: typeof e?.stack === "string" ? e.stack : undefined,
    });
  }, [error]);
  return <CrashScreen client={client} />;
}

/** THE FORM'S LAST LINE (TW-0132).
 *
 *  Chris, 2026-09-08, having found `/w/:waiver_unique_id` throwing on every waiver ever signed:
 *  "likely need to card this failure as something that should have auto-created a card, that's a
 *  critical error we never would have seen" · "i haven't looked at this screen in literally years".
 *  What a client saw was a white card with three skeleton bars: no words, no way forward, and
 *  nobody told. Two jobs here, and the second is the one that ends the silence — say something a
 *  person can act on, and REPORT.
 *
 *  ⚠ IT WRAPS `RouterProvider` AND NOTHING ELSE, which is the whole of its placement:
 *
 *    * not around `DemoDials` — wrap the pair and a crash in the demo's sales chrome takes the
 *      FORM down with it, which on the demo is the thing a shop owner came to look at;
 *    * not above the shop fetch — `ShopLoadErrorScreen` already says the better sentence for a
 *      shop that will not load, and a boundary there would paint over it.
 *
 *  It is the SECOND of the two catchers and the smaller one: everything inside a route is
 *  react-router's (RouteCrashScreen above), so what reaches here is a throw from the router shell
 *  itself. Cheap, and the only thing standing if that ever happens. */
class FormErrorBoundary extends React.Component<
  { client: Client; children?: React.ReactNode },
  { crashed: boolean }
> {
  state = { crashed: false };

  static getDerivedStateFromError() {
    return { crashed: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // TW-0500 — a draft that a reload put back could be what broke the page, so "Reload and try again" opens an empty form
    forgetEveryDraft();
    trackError(error?.message || "render_error", {
      source: "boundary",
      // React's component stack names OUR components, never a value one of them was rendering;
      // `error.stack` names our bundle's frames. Both go through the beacon's scrub anyway.
      stack: `${error?.stack || ""}\n${info?.componentStack || ""}`,
    });
  }

  render() {
    if (!this.state.crashed) return this.props.children;
    return <CrashScreen client={this.props.client} />;
  }
}

interface AppProviderProps {
  client: Client | null;
  /** TW-0505 — where the page stands on its state's rules, fetched once the state is known (stateRules.ts): Form.tsx holds a
   *  Submit until they are here and says so at the top of the form if they never came. Absent (a test's own provider, the
   *  texts page) is a page with nothing to wait on. */
  rules?: StateRulesView;
}

export const AppProvider = React.createContext<AppProviderProps>({
  client: null,
});

export const useApp = () => React.useContext(AppProvider);

function App() {
  // Paint now, learn the shop as fast as it arrives. Two sources race:
  //   1. the static file (fetchShopConfigFile) — the shop's name, mark and the two form flags;
  //   2. the API's /shop — the source of truth, and the only place the live parts come from
  //      (locked, activate_url, trial_waivers_left). It always wins when it lands, and an error
  //      screen comes only from it: "No client found", exactly as before.
  // Until either answers the form renders against a placeholder — same fields for every shop, a
  // neutral tile where the mark goes — instead of the spinner it showed until 2026-09-05.
  const subdomain = React.useMemo(currentSubdomain, []);
  // TW-0320 slice 2 — THE SHOP'S OWN PAGE FOR TEXTS, `/texts` on this same host (texts.ts, TextsPage.tsx): read once, like
  // `onTheForm` below (this app does no client-side navigation). It takes the shop's look from the two sources below and
  // nothing else of the form: no paused screen, no trial banner, no choice, no demo chrome, and its own beacon rows.
  const onTexts = React.useMemo(() => isTextsPage(), []);

  const [client, setClient] = React.useState<Client>(
    () => new Client({ name: "", subdomain, public_data: {}, placeholder: true })
  );

  // WHAT went wrong, never the sentence — the sentence is i18n.ts's and depends on the language
  // (TW-0097). "no-shop": this address has no shop behind it. "load-failed": ours did not answer.
  const [error, setError] = React.useState<ShopLoadError>(null);

  // The tab carries the shop by name — "Demo Tattoo Shop · waivers.tattoo" — from whichever source
  // names it first. index.html's "TODO: Dynamic title" sat there from 2023 until 2026-09-08.
  useEffect(() => {
    if (onTexts) document.title = TEXTS_WORDS.tab(client.name);
    else document.title = client.name ? `${client.name} · waivers.tattoo` : "waivers.tattoo";
  }, [client.name, onTexts]);

  useEffect(() => {
    let apiLanded = false;

    fetchShopConfigFile(subdomain).then((data) => {
      if (data && !apiLanded) {
        setClient((current) => (current.placeholder ? new Client({ ...data, fromFile: true }) : current));
      }
    });

    // A SIMPLE REQUEST: no Content-Type on a GET that has no body. The header this carried from 2023 made every page
    // load pay a CORS preflight (an OPTIONS round trip to the API's other origin) before the shop's truth could come
    // back; measured on a local walk 2026-09-15, one preflight per load. retrieve_shop_details reads only the query
    // string, and the live /shop answers a simple GET with `access-control-allow-origin: *`.
    fetch(`${API_URL}/shop?subdomain=${subdomain}`, {
      method: "GET",
    })
      .then((res) => {
        if (res.status === 200) {
          res
            .json()
            .then((data) => {
              apiLanded = true;
              if (data?.data?.client) {
                // The tab icon stays the brand mark (public/index.html). Until 2026-09-08 the shop's
                // logo was appended here as one more <link rel="icon"> and, declared last, it won:
                // a logo is uploaded at any size on any background — the demo shop's is 400×533 of
                // pure black on transparent — and at 16px on a dark tab strip that reads as no icon
                // at all (Chris, 2026-09-07: "i thought you fixed the favicon"). The shop's name is
                // the tab's TEXT instead (the title effect below), which reads on every strip.
                setClient(new Client(data.data.client));
              } else {
                setError("no-shop");
              }
            })
        } else {
          apiLanded = true;
          // 4xx is the API saying this subdomain is not one of ours ("Subdomain not supported" on
          // an unknown one); 5xx is ours falling over, which is worth a different sentence because
          // trying again is the right advice for exactly one of the two. A 410 is the shop having
          // closed its account (TW-0593): there IS a shop, and the client is told to ask it.
          setError(res.status >= 500 ? "load-failed" : res.status === 410 ? "closed" : "no-shop");
        }
      })
      .catch(() => {
        apiLanded = true;
        setError("load-failed");
      });
  }, [subdomain]);

  // WHAT THIS PAGE LOAD TURNED OUT TO BE (TW-0131). One report per load, and only once the page
  // knows which of the three it is: a real shop's form, the paused screen, or an address with no
  // shop behind it. `placeholder` is the fourth state — still waiting on both sources — and it is
  // deliberately never reported, because a beacon fired then would count every load twice and make
  // the denominator a number nobody could reason about.
  const reported = React.useRef(false);
  useEffect(() => {
    if (reported.current) return;
    // the texts page says what its own load was (TextsPage.tsx); an address with no shop behind it never draws it, so that
    // one is said here, as its own row and never as the form's
    if (onTexts) {
      if (error) {
        reported.current = true;
        track("texts_closed", { lang: detectLang(), reason: "no_shop", ...viaFields() });
      }
      return;
    }
    // `viaFields()` (TW-0190) is which CODE this scan came from — `?qr` a placard, `?qr=mail` a card we
    // posted, `?qr=screen` the shop app's QR screen or a screenshot of it (2026-09-30), and nothing at all
    // for the thousands printed before any tag existed. It
    // rides on all four of these because the question is about the SCAN, not about what the scan
    // found: a mailed card that lands on a shop already past its free waivers was still scanned.
    if (error) {
      reported.current = true;
      // a shop that closed its account (TW-0593) is the same row with its reason, so the read can tell it from a stranger's address
      track("form_no_shop", { lang: detectLang(), ...(error === "closed" ? { reason: "closed" } : {}), ...viaFields() });
      return;
    }
    if (client.placeholder) return;
    reported.current = true;
    track(client.locked ? "form_locked" : client.over_trial ? "form_over_trial" : "form_open", {
      shop: client.subdomain,
      lang: detectLang(undefined, undefined, enabledLangs(client.public_data)),
      service: client.public_data?.shop_type,
      ...viaFields(),
      // the demo alone says which door opened it (beacon.ts `from`); a real shop's row is byte for byte what it was
      ...(isDemoShop(client.subdomain) ? { from: referrerHost() } : {}),
    });
  }, [client, error, onTexts]);

  // THE DEMO SHOP'S STATE (TW-0104) — a hook, so it sits above the early return with the others.
  const isDemo = isDemoShop(subdomain);
  const [demoState, setDemoState] = React.useState<string | null>(() =>
    isDemo ? demoStateFromUrl(window.location.search) : null
  );
  const [demoType, setDemoType] = React.useState<string | null>(() =>
    isDemo ? demoTypeFromUrl(window.location.search) : null
  );
  // TW-0128 — the third dial: the whole guardian route, walkable on the demo without turning it on
  // for a shop with real clients.
  const [demoMinors, setDemoMinors] = React.useState<boolean>(() =>
    isDemo ? demoMinorsFromUrl(window.location.search) : false
  );
  // 2026-09-24 — the fourth dial: a photo of the birth certificate or guardianship papers when a parent
  // signs. A reply to a shop owner's question links the demo with this on (?proof=1).
  const [demoProof, setDemoProof] = React.useState<boolean>(() =>
    isDemo ? demoProofFromUrl(window.location.search) : false
  );
  // TW-0227 — WHERE IN THE STATE: `?county=Lake%20County&city=Hammond`, a link's dial with no control of its own, so a
  // reply to a shop can open the demo as a shop in a city whose rule is its own. Cleared with the state it belongs to.
  const [demoPlace, setDemoPlace] = React.useState<DemoPlace>(() =>
    isDemo ? demoPlaceFromUrl(window.location.search) : NO_DEMO_PLACE
  );
  // TW-0225 — the fifth dial: a minor's ear piercing, where the state writes the ear out of its minors rule (?ear=1)
  const [demoEar, setDemoEar] = React.useState<boolean>(() =>
    isDemo ? demoEarFromUrl(window.location.search) : false
  );

  // TW-0505 — THE STATE'S RULES, ONE STATE'S FILE (stateRules.ts). The state on the page is the one `shown` carries below: the
  // demo's dial, else the shop's own, from whichever source named it (the static file's, then the API's, which wins: a state
  // that differs is asked for in turn). Asked for the moment it is known; nothing here waits on it, and the form is drawn
  // meanwhile as a page whose state is not known yet.
  const rules = useStateRules(isDemo ? demoState : client.state);
  // a page whose state's rules never came is said on the error rail, once per state per page load (a retry that fails again
  // says nothing new), so a deploy that lost the files shows itself in the beacon before a shop writes in
  const failedState = rules.status === "failed" ? `${(isDemo ? demoState : client.state) ?? ""}` : "";
  const failuresSaid = React.useRef(new Set<string>());
  useEffect(() => {
    if (!failedState || failuresSaid.current.has(failedState)) return;
    failuresSaid.current.add(failedState);
    trackError(`state_rules_failed:${failedState}`, { source: "state_rules" });
  }, [failedState]);

  // WHICH ROUTE THIS IS, READ ONCE AT RENDER (TW-0138).
  //
  // ⚠ THE ASSUMPTION, WRITTEN DOWN: this app does NO client-side navigation. There is no <Link>,
  // no useNavigate and no useLocation anywhere in src — every link on the form and on the consent
  // page is a plain anchor with a full page load, and the one thing that rewrites the address bar
  // (`setParam` in the dials below) uses history.replaceState and never changes the PATH. So the
  // path at render is the path for the life of this tree, and a bare read needs no hook. The day
  // this app grows real navigation, this line is the one that goes stale silently.
  const onTheForm = window.location.pathname === "/";

  // TW-0136 — THE DEMO'S TWO SCREENS (pairing.ts). The laptop's half is the hook, held HERE rather than in the QR
  // modal: the phone often opens the page after the modal is closed (scan, "got it", close), and a poll that died with
  // the modal would miss exactly that. The phone's half is the effect under it. Both are the demo's only — `isDemo`
  // gates the pair of them, and on a real shop's page a `?pair=` is somebody's typing and is never read.
  const pairing = useDemoPairing(isDemo && onTheForm);
  useEffect(() => {
    if (!isDemo || !onTheForm) return;
    const id = pairIdFrom(window.location.search);
    if (!id) return;
    announcePair(id);
    // …and the address bar loses the pairing in the same breath, which is also what makes this ONCE: a second run of
    // this effect (StrictMode's, in a dev build) finds no id to announce. A reload, a share or a bookmark carries the
    // demo and not somebody's laptop.
    window.history.replaceState(null, "", withoutPairId(window.location.href));
  }, [isDemo, onTheForm]);

  // TW-0206 — WHAT THE PRINTED QR CODE OPENS (picker.ts has the rule and Chris's words, QrPicker.tsx the screen). For
  // a shop whose owner turned the choice on and whose deposit page is taking deposits, this page opens on Sign your
  // waiver · Pay a deposit instead of the form. For every other shop this goes straight to `hidden` at the first
  // answer, and the page renders exactly what it always has.
  const [picker, setPicker] = React.useState<PickerState>("undecided");
  // A CLIENT ALREADY IN A FIELD IS NEVER TAKEN OFF IT. The placeholder form is up before either source answers, and on
  // a slow line the API's answer can take two seconds: a focus inside one of its fields in that window keeps this
  // load on the form (nextPickerState). Listened for only while the page is still a placeholder.
  const touchedField = React.useRef(false);
  useEffect(() => {
    if (!client.placeholder) return;
    const onFocus = (event: FocusEvent) => {
      const target = event.target as Element | null;
      if (target && typeof target.closest === "function" && target.closest("input, textarea, select")) touchedField.current = true;
    };
    document.addEventListener("focusin", onFocus, true);
    return () => document.removeEventListener("focusin", onFocus, true);
  }, [client.placeholder]);
  const depositHref = depositPageUrl(client.subdomain);
  // a LAYOUT effect, so a shop that gets the choice never paints its real form for a frame before it
  React.useLayoutEffect(() => {
    setPicker((current) =>
      nextPickerState(current, {
        placeholder: Boolean(client.placeholder),
        offers: offersPicker(client.public_data),
        eligible: onTheForm && !isDemo && depositHref !== null,
        touched: touchedField.current,
      })
    );
  }, [client, onTheForm, isDemo, depositHref]);
  // the choice was drawn: once per load at most, because `shown` is entered only from `undecided`
  const pickerReported = React.useRef(false);
  useEffect(() => {
    if (picker !== "shown" || pickerReported.current) return;
    pickerReported.current = true;
    track("picker_open", {
      shop: client.subdomain,
      lang: detectLang(undefined, undefined, enabledLangs(client.public_data)),
      ...viaFields(),
    });
  }, [picker, client]);

  if (error) {
    return <ShopLoadErrorScreen kind={error} />;
  }

  // THE DEMO SHOP'S STATE PICKER (TW-0104; Chris, 2026-09-08: "a top demo-specific state picker doesn't
  // sound like the worst idea, for any states that are different"). Only the demo shop reads `?state=`
  // or shows the picker; a real shop's form is its own state's. The picker lists only the states whose
  // form differs from the standard one, so a shop owner sees the choice that means something.
  const shown = isDemo
    ? client.withDemo(demoState, demoType, demoMinors, demoPlace.county, demoPlace.countyKind, demoProof, demoPlace.city, demoEar)
    : client;

  const form = (
    <FormErrorBoundary client={shown}>
      <RouterProvider router={router} />
    </FormErrorBoundary>
  );

  // TW-0320 slice 2 — the texts page, in the form's place and in none of its chrome: whatever the trial says (a shop's free
  // waivers are its waiver form's, never its texts'), and on the demo too, with no dials. The boundary is the form's own.
  if (onTexts) {
    return (
      <AppProvider.Provider value={{ client: shown }}>
        <FormErrorBoundary client={shown}>
          <TextsPage client={shown} />
        </FormErrorBoundary>
      </AppProvider.Provider>
    );
  }

  // TW-0206 — the choice's two buttons. The waiver is a state change on this page (no reload: the form mounts where the
  // choice stood); the deposit is the link itself, and this only reports it on the way out (the beacon's sendBeacon
  // outlives the page).
  const pickerMeta = () => ({
    shop: shown.subdomain,
    lang: detectLang(undefined, undefined, enabledLangs(shown.public_data)),
    ...viaFields(),
  });
  const chooseWaiver = () => {
    track("picker_waiver", pickerMeta());
    setPicker("picked");
  };
  const chooseDeposit = () => track("picker_deposit", pickerMeta());

  return <AppProvider.Provider value={{
    client: shown,
    rules,
  }}>
    {/* THE DIALS BELONG TO THE FORM, AND THE FORM IS ONE ROUTE (TW-0138). Everywhere else the demo
        gets the frame and a way back instead — see DemoBackBar. Deliberately NOT a layout route:
        the comment on FormErrorBoundary above records why that boundary wraps RouterProvider and
        NOT this chrome, and a parent layout route would put the chrome back inside it. */}
    {isDemo && !shown.locked && (
      onTheForm ? (
        <DemoDials
          state={demoState}
          onState={setDemoState}
          type={demoType}
          onType={setDemoType}
          minors={demoMinors}
          onMinors={setDemoMinors}
          proof={demoProof}
          onProof={setDemoProof}
          place={demoPlace}
          onPlace={setDemoPlace}
          ear={demoEar}
          onEar={setDemoEar}
          client={shown}
          onShowQr={pairing.show}
          paused={pairing.onPhone}
        />
      ) : (
        <DemoBackBar client={shown} />
      )
    )}
    {shown.locked ? <LockedScreen client={shown} /> : (
      <>
        {/* TW-0158 — above the form, never instead of it. The demo never shows it: its own dials
            already sit at the top and the demo is a sales surface, not a shop past its trial. */}
        {shown.over_trial && !isDemo ? <TrialBanner client={shown} /> : null}
        {/* TW-0517 — a cold page nobody has claimed: one line at the top until the tenth signing, where the trial banner takes over */}
        {shown.cold && !shown.over_trial && !isDemo ? <ColdBanner client={shown} /> : null}
        {!isDemo ? (
          // TW-0206 — the choice INSTEAD of the form, only while `picker` says so. Every other load is `form`, in the very
          // place it always stood, so the form React already has is the form it keeps.
          picker === "shown" && depositHref ? (
            <QrPicker client={shown} depositHref={depositHref} onWaiver={chooseWaiver} onDeposit={chooseDeposit} />
          ) : (
            form
          )
        ) : (
          // TW-0136 — once the phone that scanned the demo's code has it open, the laptop says so INSTEAD of a form
          // nobody is filling in here. The form stays mounted underneath, `hidden`, so "Fill it in here instead" gives
          // back whatever was already typed. A real shop's page never takes this branch, so its page is what it was.
          <>
            {pairing.onPhone ? <DemoOnYourPhone client={shown} onFillHere={pairing.fillHere} /> : null}
            <div hidden={pairing.onPhone}>{form}</div>
          </>
        )}
      </>
    )}
  </AppProvider.Provider>
}

/** THE LAPTOP'S HALF OF TW-0136 (pairing.ts), as a hook App holds.
 *
 *  `show(then)` is what the QR button calls: it hands back the id the code should carry, and (re)starts the 5-minute
 *  ask. THE ID IS SPENT BY A PAIRING — the next code shown is a new one, or the phone that already announced would pair
 *  it again the instant it was shown — and it survives everything else, so a code shown twice before anyone scans is
 *  one pairing, whichever of the two drawings the phone read. `then` runs once, at the yes, beside the switch;
 *  `expired` runs once if the 5 minutes run out first. */
function useDemoPairing(enabled: boolean) {
  const [onPhone, setOnPhone] = React.useState(false);
  // ONE mutable holder for the component's life: the id not yet spent, and the poll asking about it. A holder rather
  // than two refs so the unmount below reads the poll running THEN, which is the one it has to stop.
  const [held] = React.useState<{ id: string | null; poll: PairingPoll | null }>(() => ({ id: null, poll: null }));

  // a page that goes away takes its poll with it
  React.useEffect(() => () => held.poll?.stop(), [held]);

  const show = React.useCallback(
    (then?: () => void, expired?: () => void): string | null => {
      if (!enabled) return null;
      held.id = held.id ?? mintPairId();
      const id = held.id;
      if (!id) return null;
      held.poll?.stop();
      held.poll = pollForPhone(id, {
        onPaired: () => {
          held.id = null;
          held.poll = null;
          setOnPhone(true);
          then?.();
        },
        // the listening stopped and the id is kept: the next code shown carries it again, so a phone that read this
        // one late still pairs the moment the laptop is asking again
        onExpired: () => {
          held.poll = null;
          expired?.();
        },
      });
      return id;
    },
    [enabled, held]
  );

  const fillHere = React.useCallback(() => setOnPhone(false), []);

  return { onPhone, show, fillHere };
}

/** WHAT THE LAPTOP SAYS ONCE THE PHONE HAS IT (TW-0136). The heading is Chris's own sentence from the card. It stands
 *  where the form was — a form nobody is filling in on this screen — and it offers the way back, which returns the form
 *  exactly as it was left (App keeps it mounted underneath).
 *
 *  FOCUS MOVES TO ITS HEADING WHEN IT APPEARS. A live region inserted with its words already in it is one most screen
 *  readers never announce, and focus that was in the form would fall to the page when the form went hidden. A focused
 *  heading is read on arrival, and it puts a keyboard where the next thing is. */
function DemoOnYourPhone({ client, onFillHere }: { client: Client; onFillHere: () => void }) {
  const T = strings(detectLang(window.location.search, undefined, enabledLangs(client.public_data)));
  const heading = React.useRef<HTMLHeadingElement>(null);
  React.useEffect(() => {
    heading.current?.focus();
  }, []);
  return (
    <BasePage>
      <div data-testid="demo-on-your-phone" className="flex flex-col items-center gap-y-3 py-16 text-center">
        <Smartphone size={40} aria-hidden="true" />
        <h1 ref={heading} tabIndex={-1} className="text-2xl font-bold">
          {T.demoOnPhoneTitle}
        </h1>
        <p className="max-w-md text-gray-700">{T.demoOnPhoneBody}</p>
        <button
          type="button"
          data-testid="demo-fill-here"
          className="mt-3 rounded border border-black px-4 py-2 font-semibold"
          onClick={onFillHere}
        >
          {T.demoOnPhoneBack}
        </button>
      </div>
    </BasePage>
  );
}

/** THE DEMO'S CHROME EVERYWHERE THAT IS NOT THE FORM (TW-0138).
 *
 *  Chris, 2026-09-08, on demo.waivers.tattoo/terms-and-conditions: "perhaps the right selectors are
 *  gone and it's a 'back to waiver' button. this is only for demo mode. can be carded, and i'm not
 *  worried about it now."
 *
 *  He is right about the small thing and the small thing is the whole card: a state picker on a page
 *  of static consent text is a control that does nothing, which is worse than no control — a shop
 *  owner reaches for it, the page does not move, and what they learn is that the demo is broken.
 *
 *  SO THE FRAME STAYS AND THE DIALS GO. Same black bar, same dashed red border, same wordmark to
 *  the front of the business — that frame is what says "you are still in a demo", and on the consent
 *  page, which is a real shop's real consent page in every other respect, it is the only thing that
 *  does. What replaces the dials is the one thing this page owes a reader: the way back.
 *
 *  IT IS TWO ITEMS AND IT IS BUILT LIKE TWO ITEMS. None of the dial bar's `order-*` / `lg:` / `xl:`
 *  machinery is copied down here: every one of those numbers was measured against four specific
 *  items at three specific widths (see below), and a wordmark plus a link wraps correctly on a
 *  390px phone with no help at all.
 *
 *  THE LINK CARRIES THE DIALS. They live in the query string, so a bare "/" would land a reader on
 *  a demo reset to its defaults — losing the Oregon minors configuration they built two clicks ago,
 *  which is its own small wrong thing. `?lang=` rides along the same way: the consent page opens in
 *  its own tab and takes its language off the URL, so the way home has to hand it back. */
function DemoBackBar({ client }: { client: Client }) {
  const lang = detectLang(window.location.search, undefined, enabledLangs(client.public_data));
  const T = strings(lang);
  // the form is "/", plus the language when it is not English, plus whatever dials are set
  const href = withDemoQuery(lang === "es" ? "/?lang=es" : "/", window.location.search);
  return (
    <div
      className="w-full bg-black text-white px-3 sm:px-4 py-3 flex flex-row flex-wrap items-center gap-x-4 gap-y-2"
      style={{ borderBottom: "4px dashed #c8412f" }}
      data-testid="demo-back-bar"
    >
      <a href={siteHref(MARKETING_URL)} className="shrink-0 leading-none" aria-label="waivers.tattoo">
        <img src="/brand/wordmark-on-ink.svg" alt="waivers.tattoo" className="h-[16px] sm:h-[22px] w-auto" />
      </a>
      <a
        href={href}
        data-testid="demo-back-to-waiver"
        className="ms-auto inline-flex items-center rounded border border-white px-3 py-1 text-sm font-semibold whitespace-nowrap"
      >
        {T.demoBackToWaiver}
      </a>
    </div>
  );
}

/** THE DEMO'S OWN CHROME (TW-0104, rebuilt twice on 2026-09-08 — TW-0135).
 *
 *  Chris's shape: "wordmark left, show qr code middle, customize right and even on desktop it could
 *  be collapsed, and be clear ... or maybe there's a state picker + customize further?"
 *
 *  THE FIRST BUILD PUT ALL THREE DIALS IN THE BAR AND HE READ IT AS TOO BUSY, correctly: two prose
 *  labels, two selects and a long checkbox label, all competing across the top of a page whose
 *  actual job is a consent form. So the bar is four things now — wordmark · QR · the ONE dial a
 *  shop owner came for · a door to the rest — and it is collapsed on every width, not only on a
 *  phone.
 *
 *  THE STATE STAYS OUT, and that is his second idea rather than a compromise on his first. It is
 *  the dial a shop owner wants before any other ("what does MY state's form look like?"), and one
 *  obviously-live control is what teaches that this bar is interactive at all — a row of static
 *  buttons would hide the whole feature behind a word. On a phone it wraps to its own line rather
 *  than disappearing: one select is not what made the old bar busy, and a phone deserves the dial
 *  that matters as much as a laptop does.
 *
 *  THE QR SITS AT THE END, not in the middle. It was centred first, on his own first sketch, and he
 *  read it back: "maybe Show QR code to scan with your phone actually belongs right of Customize
 *  this demo? it's kinda weird in the middle maybe." It is — the wordmark is the only thing that is
 *  not a control, so everything you can press belongs in one group and the middle is the one place
 *  a button has nothing to be next to.
 *
 *  Only the demo shop shows any of this (subdomain.ts); the dashed red frame says so. Each dial
 *  writes its query key, so the URL is shareable — and the QR is that URL, which is how the dials
 *  ride along to the phone rather than resetting on it. */
function DemoDials({ state, onState, type, onType, minors, onMinors, proof, onProof, place, onPlace, ear, onEar, client, onShowQr, paused }: {
  state: string | null; onState: (s: string | null) => void;
  type: string | null; onType: (t: string | null) => void;
  minors: boolean; onMinors: (m: boolean) => void;
  /** 2026-09-24 — the fourth dial, under the minors switch and only while that one is on */
  proof: boolean; onProof: (p: boolean) => void;
  /** TW-0227 — where in the state (`?county=` / `?city=`), a link's dial: shown in the door's summary, cleared with the state */
  place: DemoPlace; onPlace: (p: DemoPlace) => void;
  /** TW-0225 — the fifth, beside it: a minor's ear piercing */
  ear: boolean; onEar: (e: boolean) => void;
  client: Client;
  /** TW-0136 — App's pairing (useDemoPairing): the id the code should carry, what to do at the yes, and what to do if
   *  the 5 minutes run out first. */
  onShowQr: (then?: () => void, expired?: () => void) => string | null;
  /** TW-0136 — the laptop is showing "You're completing this on your phone". EVERY CONTROL THAT CHANGES THE FORM IS OFF
   *  while it does: a dial moved here reaches no phone, and it changes the form hidden under the panel. That second half
   *  is a real bug, not tidiness: a signature pad measures its canvas once, when it mounts (react-signature-canvas
   *  `_resizeCanvas`, never again under `clearOnResize={false}`), so a guardian pad a dial mounted inside the hidden
   *  form would come back 0 by 0 and take no signature. */
  paused: boolean;
}) {
  const lang = detectLang(window.location.search, undefined, enabledLangs(client.public_data));
  const T = strings(lang);
  const [open, setOpen] = React.useState(false);
  const [qrOpen, setQrOpen] = React.useState(false);
  const [qrPairId, setQrPairId] = React.useState<string | null>(null);
  const setParam = (key: string, value: string | null) => {
    const url = new URL(window.location.href);
    if (value) url.searchParams.set(key, value); else url.searchParams.delete(key);
    window.history.replaceState(null, "", url.toString());
  };
  /** WHAT THE DEMO REPORTS (TW-0135). One event per dial move, carrying the whole configuration it
   *  moved TO rather than the one field that changed — so a single row is a whole answer ("someone
   *  looked at Georgia, piercings, minors on") instead of a puzzle to reassemble in a query. The
   *  three values are picked from fixed lists and never typed; beacon.ts carries the reasoning. */
  const reportDials = (next: { state?: string | null; type?: string | null; minors?: boolean }) => {
    const dials = { state, type, minors, ...next };
    track("demo_dial", {
      shop: client.subdomain,
      lang,
      demoState: dials.state ?? undefined,
      demoType: dials.type ?? "tattoo",
      demoMinors: dials.minors ? true : undefined,
    });
  };
  // TW-0279 — what the demo shop does, a cosmetic studio included (`?type=cosmetic` sets `cosmetic: "only"`, withDemo)
  const kind: DemoType = cosmeticOffer(client.public_data?.cosmetic) === "only" ? "cosmetic" : shopType(client.public_data?.shop_type);
  const changes = demoChanges({ state, type: kind, minors, proof, ear, ...place }, T, lang);

  // ONE state picker, in the bar itself, wrapping to its own line on a narrow screen rather than
  // being duplicated into the panel below. A `hidden sm:flex` copy plus a `sm:hidden` copy is two
  // live controls in the DOM at all times, which is two of everything to a screen reader — and the
  // reason to notice it here is that no screenshot at any width would ever show it.
  const statePicker = (
    <label className="order-3 w-full lg:w-auto lg:ms-auto flex flex-col sm:flex-row sm:items-center gap-x-3 gap-y-1">
      <span className="font-semibold">{T.demoPickState}</span>
      <select
        className="w-full sm:w-auto text-black rounded px-2 py-1 disabled:opacity-60"
        data-testid="demo-state-select"
        disabled={paused}
        value={state ?? ""}
        onChange={(e) => {
          const next = e.target.value || null;
          onState(next);
          setParam("state", next);
          // TW-0227 — a county belongs to its state: a leftover Lake County under an Ohio demo is no place at all
          onPlace(NO_DEMO_PLACE);
          setParam("county", null);
          setParam("city", null);
          reportDials({ state: next });
        }}
      >
        <option value="">{T.demoStandard}</option>
        {/* ALL 51. It used to list only the states whose form differs, which reads as the list of
            states we serve — a shop in the other 42 scrolls past their own and concludes we do not
            cover them (demo.ts carries the whole reasoning). What that short list signalled now
            lives in the line under this bar. */}
        {everyState().map((s) => (
          <option key={s.abbr} value={s.abbr}>{s.name}</option>
        ))}
      </select>
    </label>
  );

  // 2026-09-24 — THE DOOR SAYS WHAT IS SET, AND THE DIALS LIVE IN A MODAL. Chris, landing cold on the
  // dialled link he had just posted: "the only nit is that 'what this shop does' likely should be expanded
  // when you land on that link cold as the header and subtitles are there" — then, seeing the panel open
  // with its five lines: "is a beast of a header ... say something simply and make it clickable and
  // customizable". So the bar stays ONE row. The door's label is the one-line summary of the dials that are
  // set (a person arriving on a shared link sees what it is set to without opening anything), pressing it
  // opens the controls with the explanation lines under them, and the state picker stays in the bar: it is
  // the dial people come for (2026-09-08). The modal is in the DOM closed (hidden), the way the panel was,
  // so nothing about which control exists depends on the door.
  const set = [
    // TW-0227 — the place first when a link set one ("Hammond, Lake County"): it is what the link is FOR
    demoPlaceLabel(place) || null,
    type === "both" ? T.demoTypeBoth : type === "piercing" ? T.demoTypePiercing : type === "cosmetic" ? T.demoTypeCosmetic : null,
    minors ? T.demoMinorsShort : null,
    proof ? T.demoProofShort : null,
    ear ? T.demoEarShort : null,
  ].filter((x): x is string => Boolean(x));
  const summary = set.join(" · ");
  // on a phone the door is 64vw and the full summary cut mid-word ("Under 1…", measured on prod at 390
  // wide, 2026-09-24 8:09pm): the first dial and a count of the rest instead, the whole line on `sm` and up
  const short = set.length > 1 ? `${set[0]} +${set.length - 1}` : summary;
  const reset = () => {
    onState(null);
    onType(null);
    onMinors(false);
    onProof(false);
    onPlace(NO_DEMO_PLACE);
    onEar(false);
    // …and the modal closes with them (Chris: "reset doesn't seem to toggle the What this shop does to
    // closed but i kinda expected it"). A reset that leaves the door open has put the dials back and not
    // the screen, which is half a reset.
    setOpen(false);
    const url = new URL(window.location.href);
    ["state", "type", "minors", "proof", "county", "city", "ear"].forEach((k) => url.searchParams.delete(k));
    window.history.replaceState(null, "", url.toString());
    // what they were LOOKING at when they gave up on it, which is the interesting half
    track("demo_reset", { shop: client.subdomain, lang, demoState: state ?? undefined });
  };

  return (
    <div
      // ONE ROW that wraps. A PHONE READS: wordmark · the door, then the state under them (Chris: "all of
      // the mobile tests i did push it a bit. maybe top right is What this shop does and middle is the
      // state picker?"). A DESKTOP READS: wordmark, centred against the bar · state · door · QR. Two
      // measured breakpoints: `lg` (1024) is where the QR joins and the controls gather right, `xl` (1280)
      // where the wordmark leaves the flow (its reserved column costs 210). `sm` broke it: four rows.
      className="w-full bg-black text-white px-3 sm:px-4 py-3 xl:relative xl:ps-[210px] flex flex-row flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-2"
      style={{ borderBottom: "4px dashed #c8412f" }}
      data-testid="demo-state-picker"
      data-demo-chrome=""
    >
      {/* THE REAL WORDMARK (Chris: "this doesn't seem to be our marketing wordmark right?"): the on-ink
          cut of the outlined SVG the site and the shop app use, a FILE in public/ so a client's phone
          never renders this chrome's bytes; design/brand/sync.sh keeps it here. It is the way back to the
          front of the business (Chris: "clicking the wordmark likely should go to the root"). 16px on a
          phone, 22 on a laptop — the mark is 8.5:1, and at 18px the door wrapped on every phone under 390. */}
      <a
        href={siteHref(MARKETING_URL)}
        className="order-1 shrink-0 leading-none xl:absolute xl:start-4 xl:top-1/2 xl:-translate-y-1/2"
        aria-label="waivers.tattoo"
      >
        <img src="/brand/wordmark-on-ink.svg" alt="waivers.tattoo" className="h-[16px] sm:h-[22px] w-auto" />
      </a>

      <button
        type="button"
        data-testid="demo-customize-button"
        aria-expanded={open}
        aria-controls="demo-dials"
        aria-haspopup="dialog"
        title={T.demoCustomize}
        // ⚠ `sm:ms-0` IS LOAD-BEARING: on a phone this pushes the door to the top-right beside the wordmark;
        // on a laptop the STATE PICKER carries the ms-auto, and two auto margins in one row split the space.
        className="order-2 lg:order-4 ms-auto lg:ms-0 inline-flex items-center gap-x-1 rounded border border-white px-2 sm:px-3 py-1 text-sm font-semibold max-w-[64vw] sm:max-w-none"
        onClick={() => setOpen((was) => !was)}
      >
        <span className="truncate sm:hidden">{short || T.demoCustomize}</span>
        <span className="hidden sm:inline truncate">{summary || T.demoCustomize}</span>
        {open ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
      </button>

      {statePicker}

      {/* LAST, and desktop only: the whole point of it is to move you to a device you are not on,
          so on that device it is nothing but a line taking up a phone's width. */}
      <button
        type="button"
        data-testid="demo-qr-button"
        disabled={paused}
        className="order-5 hidden lg:inline-flex items-center rounded border border-white px-3 py-1 text-sm font-semibold whitespace-nowrap disabled:opacity-50"
        onClick={() => {
          // TW-0136 — the code carries a pairing, and App starts asking whether a phone has opened it. At the yes the
          // code closes, if it is still open, and the demo reports the move it exists to show.
          setQrPairId(
            onShowQr(
              () => {
                setQrOpen(false);
                track("demo_paired", { shop: client.subdomain, lang, demoState: state ?? undefined });
              },
              // the laptop stopped listening, so the code goes too: one showing now could pair nothing
              () => setQrOpen(false)
            )
          );
          setQrOpen(true);
          track("demo_qr", { shop: client.subdomain, lang, demoState: state ?? undefined });
        }}
      >
        {T.demoShowQr}
      </button>

      {/* WHAT IS BEHIND THE DOOR: the dials a shop owner reaches for second, in a modal (Chris, 2026-09-24:
          "we can always put that in a simple modal ... say something simply and make it clickable and
          customizable"). The backdrop and Escape close it; Done closes it; Reset closes it with the dials. */}
      <DemoModal id="demo-dials" testid="demo-dials" open={open} onClose={() => setOpen(false)} title={T.demoCustomize}>
          <label className="flex flex-col gap-y-1">
            <span className="font-semibold">{T.demoPickType}</span>
            <select
              className="w-full rounded border border-gray-400 px-2 py-2 disabled:opacity-60"
              value={type ?? ""}
              data-testid="demo-type-picker"
              disabled={paused}
              onChange={(e) => {
                const next = e.target.value || null;
                onType(next);
                setParam("type", next);
                reportDials({ type: next });
              }}
            >
              <option value="">{T.demoTypeTattoo}</option>
              <option value="both">{T.demoTypeBoth}</option>
              <option value="piercing">{T.demoTypePiercing}</option>
              {/* TW-0279 — a link's dial (`?type=cosmetic`): in the list only while the link carries it, so the cosmetic
                  form can be walked and linked while it ships dark, and the select never paints a value it does not list */}
              {type === "cosmetic" && <option value="cosmetic">{T.demoTypeCosmetic}</option>}
            </select>
          </label>
          {/* TW-0128 — the shop's own setting, off by default here as it is everywhere. The state still
              decides: turning it on in a state that says no shows the state's refusal, which is the half
              worth demonstrating, and the lines below say which case you are in. ⚠ DEAD UNTIL A STATE IS
              PICKED (Chris: "...but they haven't selected the state?"): refusing the tick says so where the
              hand already is. `?minors=1` with no state can still arrive by URL, which is why demo.ts
              keeps its own line for that case. */}
          <label
            className={`flex flex-row items-start gap-x-3 ${state ? "" : "opacity-60"}`}
            title={state ? undefined : T.demoMinorsPickState}
          >
            <input
              type="checkbox"
              className="mt-1 shrink-0"
              style={{ accentColor: "#c8412f" }}
              data-testid="demo-minors-toggle"
              checked={minors}
              disabled={!state || paused}
              onChange={(e) => {
                const next = e.target.checked;
                onMinors(next);
                setParam("minors", next ? "1" : null);
                // 2026-09-24 — the fourth dial rides on this one: off with it, so a URL never says
                // "proof" for a demo that asks no parent for anything
                if (!next) {
                  onProof(false);
                  setParam("proof", null);
                  // TW-0225 — and the fifth: an ear piercing is a minor's question here
                  onEar(false);
                  setParam("ear", null);
                }
                // TICKING IT CARRIES THE TYPE WITH IT (Chris: "if a user checks We pierce minors but
                // haven't flipped Tattoos only to something, it should go to Tattoos and piercings i
                // think right?"). Right: a shop that pierces minors pierces. BOTH rather than Piercings
                // only, because they had it on tattoos — adding piercings is the smaller true change.
                // TW-0279 — only a TATTOO-only demo is carried: a cosmetic studio's has no piercing to add
                const carried = next && servicesOf(client.public_data).join(",") === "tattoo";
                if (carried) {
                  onType("both");
                  setParam("type", "both");
                }
                reportDials({ minors: next, ...(carried ? { type: "both" } : {}) });
              }}
            />
            <span className="flex-1 font-semibold">
              {T.demoPierceMinors}
              {!state && (
                <span className="block text-sm font-normal text-gray-600" data-testid="demo-minors-needs-state">
                  {T.demoNeedsState}
                </span>
              )}
            </span>
          </label>
          {/* 2026-09-24 — THE FOURTH DIAL, and only once the third is on: a photo of the birth certificate
              or guardianship papers when a parent signs. On a real shop it is the owner's switch in Settings
              (Who can sign), on by the rule where the state names the proof. Kelly Voris of The Tattoo Lady
              (Hammond IN) asked on the ad the day it was built, and the reply carries this demo with the box
              ticked (?proof=1). */}
          {minors && (
            <label className="flex flex-row items-start gap-x-3">
              <input
                type="checkbox"
                className="mt-1 shrink-0"
                style={{ accentColor: "#c8412f" }}
                data-testid="demo-proof-toggle"
                checked={proof}
                disabled={paused}
                onChange={(e) => {
                  const next = e.target.checked;
                  onProof(next);
                  setParam("proof", next ? "1" : null);
                }}
              />
              <span className="flex-1 font-semibold">{T.demoAskProof}</span>
            </label>
          )}
          {/* TW-0225 — THE FIFTH DIAL, beside the fourth and only once the minors switch is on: the demo client is here
              for an ear piercing, so its spot starts at Earlobe, and the line under the dials says what the picked
              state's rule does with one (Michigan writes the whole ear out of it; Florida only a lobe pierced with a
              stud-and-clasp gun; Ohio pulls it in). Dagger Face (MI) pierces minors. */}
          {minors && (
            <label className="flex flex-row items-start gap-x-3">
              <input
                type="checkbox"
                className="mt-1 shrink-0"
                style={{ accentColor: "#c8412f" }}
                data-testid="demo-ear-toggle"
                checked={ear}
                disabled={paused}
                onChange={(e) => {
                  const next = e.target.checked;
                  onEar(next);
                  setParam("ear", next ? "1" : null);
                }}
              />
              <span className="flex-1 font-semibold">{T.demoEar}</span>
            </label>
          )}
          {/* WHAT THIS CONFIGURATION CHANGES — one line per dial that is not where it started, and nothing
              at all when they all are (Chris: "i don't mind the extra lines of help text as it's uncommon
              and truly helpful"). demo.ts decides what they say; this only draws them. Reset closes the
              list it is under (Chris: "what about a reset state?"): present exactly when there is
              something to undo. */}
          {changes.length > 0 && (
            <div className="text-sm text-gray-700 flex flex-col gap-y-1 border-t border-gray-200 pt-3" data-testid="demo-state-adds">
              {changes.map((line) => (
                <span key={line}>{line}</span>
              ))}
              <button
                type="button"
                data-testid="demo-reset"
                disabled={paused}
                className="self-start underline text-sm mt-1 disabled:opacity-50"
                onClick={reset}
              >
                {T.demoReset}
              </button>
            </div>
          )}
          <button
            type="button"
            data-testid="demo-done"
            className="self-end rounded bg-black px-4 py-2 font-semibold text-white"
            onClick={() => setOpen(false)}
          >
            {T.demoDone}
          </button>
      </DemoModal>
      {qrOpen && <DemoQr onClose={() => setQrOpen(false)} T={T} pairId={qrPairId} />}
    </div>
  );
}
/** ONE SHELL FOR THE DEMO'S TWO MODALS (2026-09-24; Chris: "i don't think we had Fable on the QR modal so if
 *  you end up improving both, great"): the same backdrop, box, title and closing (the backdrop, Escape, the
 *  button), so the dials and the code read as one piece of chrome. `open` decides hidden or shown and the
 *  shell stays in the DOM either way: the dials' controls exist whether or not the door is open, which is
 *  what the tests and the URL both lean on; the QR modal mounts only while it is up, as it always did. */
function DemoModal({
  id,
  testid,
  open,
  onClose,
  title,
  children,
}: {
  id?: string;
  testid: string;
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  const titleId = `${testid}-title`;
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  return (
    <div
      id={id}
      data-testid={testid}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className={`${open ? "flex" : "hidden"} fixed inset-0 z-50 items-center justify-center bg-black/70 p-4`}
      onClick={onClose}
    >
      <div
        className="bg-white text-black rounded-lg p-6 w-full max-w-lg flex flex-col gap-y-4 shadow-xl"
        style={{ border: "3px solid #17140F" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} className="text-xl font-bold">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

/** The QR modal: this exact URL, dials and all, so the phone opens the demo you configured rather
 *  than a fresh one. The library that draws it is loaded on demand (qr.ts) — this bundle is what
 *  every client at every counter downloads, for a feature exactly one shop can reach. The address the
 *  code opens is printed under it, so a person can see the code is THIS demo and not a fresh one. */
function DemoQr({ onClose, T, pairId }: { onClose: () => void; T: ReturnType<typeof strings>; pairId: string | null }) {
  const [path, setPath] = React.useState<string | null>(null);
  // TW-0136 — plus the pairing, so the phone that opens it can tell this laptop (pairing.ts). No id (a browser with no
  // random source) is the plain URL, which is exactly the code this modal drew before.
  const href = pairId ? withPairId(window.location.href, pairId) : window.location.href;
  const shown = `${window.location.host}${window.location.pathname === "/" ? "/" : window.location.pathname}${window.location.search}`;
  React.useEffect(() => {
    let alive = true;
    loadQrModules(href).then((modules) => {
      if (alive) setPath(qrPath(modules, 260));
    });
    return () => { alive = false; };
  }, [href]);
  return (
    <DemoModal testid="demo-qr-modal" open onClose={onClose} title={T.demoQrTitle}>
      <div className="self-center rounded bg-white p-3" style={{ border: "3px solid #17140F", boxShadow: "6px 6px 0 #17140F" }}>
        {path ? (
          <svg width={260} height={260} viewBox="0 0 260 260" role="img" aria-label={T.demoQrTitle}>
            <path d={path} fill="#17140F" />
          </svg>
        ) : (
          <div style={{ width: 260, height: 260 }} />
        )}
      </div>
      <p className="text-center text-xs text-gray-600 break-all" style={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
        {shown}
      </p>
      <p className="text-sm text-gray-700 text-center">{T.demoQrNote}</p>
      <button type="button" className="self-end rounded bg-black px-4 py-2 font-semibold text-white" onClick={onClose}>
        {T.demoQrClose}
      </button>
    </DemoModal>
  );
}

export default App;
